import { PureComponent,  createElement } from 'react';
import PropTypes from 'prop-types';
import hoistStatics from 'hoist-non-react-statics';
import shallowEqual from 'shallowequal';
import has from 'lodash/has';
import set from 'lodash/set';
import values from 'lodash/values';
import flatMap from 'lodash/flatMap';
import zipObject from 'lodash/zipObject';
import merge from 'lodash/merge';
import uniq from 'lodash/uniq';
import { expand, isNotFound, isPending, isError, MusterError } from '../../muster-utils';

const SOURCE_PATH_WILDCARD_PATTERN = /\[([^\]]+)\]/g;
const TARGET_PATH_WILDCARD_PATTERN = /\*/g;

function isLeafNode(value) {
  return (
    (typeof value === 'string') ||
    ((typeof value === 'object') && (value !== null) && (
      ('$get' in value) ||
      ('$set' in value) ||
      ('$call' in value)
    ))
  );
}

function getQueryPaths(queries, verb) {
  return flatMap(Object.keys(queries), (key) => {
    const value = getNormalisedNode(queries[key]);
    const matchedPaths = isLeafNode(value) && (verb in value) ? [value[verb]] : [];
    const childPaths = isLeafNode(value) ? [] : getQueryPaths(value, verb);
    return [...matchedPaths, ...childPaths];
  });
}

function getQueryAliases(queries, verb, parentPath = null) {
  return flatMap(Object.keys(queries), (key) => {
    const value = getNormalisedNode(queries[key]);
    const prefixedPath = joinPathSegments(parentPath, key);
    const matchedPaths = isLeafNode(value) && (verb in value) ? [prefixedPath] : [];
    const childPaths = isLeafNode(value) ? [] : getQueryAliases(value, verb, prefixedPath);
    return [...matchedPaths, ...childPaths];
  });
}

function joinPathSegments(...args) {
  return args.filter((value) => value).join('.');
}

function getPathMappings(queries, verb) {
  const sourcePaths = getQueryPaths(queries, verb);
  const aliasedPaths = getQueryAliases(queries, verb);
  return zipObject(aliasedPaths, sourcePaths);
}

function normaliseQueries(queries) {
  const fragmentKeys = Object.keys(queries);
  return zipObject(
    fragmentKeys,
    fragmentKeys.map((key) => {
      const value = getNormalisedNode(queries[key]);
      return (isLeafNode(value) ? value : normaliseQueries(value));
    })
  );
}

function getNormalisedNode(value) {
  if (value && !isLeafNode(value)) { return value; }
  const node = (typeof value === 'string') ? { $get: value } : value;
  const fragmentPath = parseNodePath(node);
  if (!fragmentPath) { throw new Error(`Invalid query fragment: ${fragmentPath}`); }
  return node;
}

function parseNodePath(node) {
  if (!node) { return null; }
  if ('$get' in node) { return node.$get; }
  if ('$set' in node) { return node.$set; }
  if ('$call' in node) { return node.$call; }
  return null;
}

function bindQueriesToComponent(queries, WrappedComponent) {
  class QueriedComponent extends PureComponent {
    constructor(props, context) {
      super(props, context);
      const initialTree = merge({}, this.createBoundSetters(), this.createBoundCallers());
      this.tree = initialTree;
      this.state = {
        tree: this.tree,
      };
      this.subscription = null;
    }

    componentDidMount() {
      this.subscription = this.subscribe(this.context);
    }

    componentWillReceiveProps(nextProps, nextContext) {
      if (this.subscription && !shallowEqual(this.context, nextContext)) {
        this.subscription.unsubscribe();
        this.subscription = this.subscribe(nextContext);
      }
    }

    componentWillUnmount() {
      this.subscription.unsubscribe();
      this.subscription = null;
    }

    subscribe(context) {
      const pathMappings = getPathMappings(queries, '$get');
      const sourcePaths = uniq(values(pathMappings));
      const sourceMappings = getReversedPathMappings(pathMappings);

      return context.subscribe(sourcePaths, (pathValues) => {
        pathValues.forEach(({ value }) => {
          if (isError(value)) {
            const error = value.value;
            throw new MusterError(error.message, error);
          }
        });
        const updatedTree = getModifiedTree(this.tree, pathValues, sourceMappings);
        this.tree = updatedTree;
        this.setState({
          tree: this.tree,
        });
      });
    }

    createBoundSetters() {
      const pathMappings = getPathMappings(queries, '$set');
      const tree = {};
      Object.keys(pathMappings)
        .filter((targetPath) => !isWildcardTargetPath(targetPath))
        .forEach((targetPath) => {
          const sourcePath = pathMappings[targetPath];
          set(tree, targetPath, (value) => {
            this.context.set(sourcePath, value);
          });
        });
      return tree;
    }

    createBoundCallers() {
      const pathMappings = getPathMappings(queries, '$call');
      const tree = {};
      Object.keys(pathMappings)
        .filter((targetPath) => !isWildcardTargetPath(targetPath))
        .forEach((targetPath) => {
          const sourcePath = pathMappings[targetPath];
          set(tree, targetPath, (...args) => {
            this.context.call(sourcePath, ...args);
          });
        });
      return tree;
    }

    render() {
      return createElement(WrappedComponent, Object.assign({},
        this.props,
        this.state.tree
      ));
    }
  }

  QueriedComponent.contextTypes = {
    subscribe: PropTypes.func,
    set: PropTypes.func,
    call: PropTypes.func,
  };

  return hoistStatics(QueriedComponent, WrappedComponent);
}

function isWildcardTargetPath(path) {
  return TARGET_PATH_WILDCARD_PATTERN.test(path);
}

function getReversedPathMappings(targetMappings) {
  const expandedMappings = zipObject(
    Object.keys(targetMappings),
    values(targetMappings).map((sourcePath) => expand(sourcePath))
  );
  return Object.keys(expandedMappings).reduce(
    (reversedMappings, targetPath) => {
      const sourcePaths = expandedMappings[targetPath];
      const sourceMappings = sourcePaths.reduce((mappings, sourcePath) =>
        Object.assign(mappings, {
          [sourcePath]: (reversedMappings[sourcePath] || []).concat(
            getExpandedTargetPath(sourcePath, targetPath)
          ),
        }),
        {}
      );
      return Object.assign(reversedMappings, sourceMappings);
    }, {}
  );
}

function splitPath(path) {
  const matches = path.match(/"(.*?)"|([^."\s]+)/g);
  // Replace double quotes surrounding keys
  return matches
    .map((str) => str.replace(/^["'](.+(?=["']$))["']$/, '$1'))
    .map(convertArrayKeyToNumber);
}

function convertArrayKeyToNumber(key) {
  return isNumeric(key) ? Number(key) : key;
}

function isNumeric(string) {
  return !isNaN(parseFloat(string));
}

function getModifiedTree(tree, pathValues, sourceMappings) {
  return pathValues.reduce((updatedTree, { path, value }) => {
    const targetPaths = sourceMappings[path];
    const parsedValue = parseValue(value);
    return targetPaths.reduce((currentTree, targetPath) => {
      const pathSegments = splitPath(targetPath);
      // Avoid creating empty parent nodes in the props tree for pending values
      const isInitialPendingValue = (parsedValue === undefined) && !has(updatedTree, pathSegments);
      if (isInitialPendingValue) { return currentTree; }
      // Return a modified tree that contains the updated path value
      return getTreeWithMutations(currentTree, pathSegments, parsedValue);
    }, updatedTree);
  }, tree);

  function getTreeWithMutations(parentNode, pathSegments, value) {
    const keyName = pathSegments[0];
    const isTargetPath = (pathSegments.length <= 1);
    const nodeValue = (isTargetPath ? value :
      getBranchWithMutations(parentNode[keyName], pathSegments.slice(1), value)
    );
    if (Array.isArray(parentNode)) {
      const parentArrayClone = parentNode.slice();
      parentArrayClone[keyName] = nodeValue;
      return parentArrayClone;
    }
    return ({ ...parentNode, [keyName]: nodeValue });
  }

  function getBranchWithMutations(existingNode, childPathSegments, value) {
    // When creating new branches, if the immediate child key is numeric then assume it's an array
    const branchNode = existingNode || (typeof childPathSegments[0] === 'number' ? [] : {});
    return getTreeWithMutations(branchNode, childPathSegments, value);
  }
}

function parseValue(value) {
  if (isNotFound(value)) { return null; }
  if (isPending(value)) { return undefined; }
  return value;
}

function getExpandedTargetPath(sourcePath, targetPath) {
  const wildcards = getAllRegExpMatches(sourcePath, SOURCE_PATH_WILDCARD_PATTERN, {
    captureIndex: 1,
  });
  let matchIndex = -1;
  return targetPath.replace(TARGET_PATH_WILDCARD_PATTERN, () => wildcards[++matchIndex]);
}

function getAllRegExpMatches(string, pattern, { captureIndex = 0 }) {
  let result;
  const matches = [];
  while (result = pattern.exec(string)) { // eslint-disable-line no-cond-assign
    const match = result[captureIndex];
    matches.push(match);
  }
  return matches;
}

/**
 * Connect a pure component to the muster graph.
 *
 * @param  {Object<string, object>|Object<string, string>} queries A list
 * of bound fragments or full path strings to pass to muster.
 *
 * @return {React.Component} A connected component whose props are updated via
 * the queries.
 *
 * @example
 * query({
 *   name: 'highestPriorityTodo.name'
 * })((props) =>
 *   <div>{props.name}</div>
 * )
 *
 * @example
 * query({
 *   todo: bind('highestPriorityTodo', todoView.todo),
 * })((props) =>
 *   <div>{props.todo.name}</div>
 * )
 */
function query(queries) {
  return (WrappedComponent) =>
    bindQueriesToComponent(normaliseQueries(queries), WrappedComponent);
}

export default query;
export { isLeafNode };
